import { type Account } from '@/api/generated/model';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountSourceBadge } from '@/components/wealth/AccountSourceBadge';
import { SyncBadge } from '@/components/wealth/SyncBadge';
import { formatCurrency, getFullDate, getRelativeTime, useDefaultCurrency } from '@/lib/utils';
import { Building, Building2, Coins, CreditCard, Home, PiggyBank, Wallet } from 'lucide-react';

interface AccountListItemProps {
  account: Account;
  onSelect: (account: Account) => void;
  isSelected?: boolean;
}

const getAccountIcon = (type: string) => {
  switch (type) {
    case 'bank':
      return <Building className='h-4 w-4' />;
    case 'investment':
      return <PiggyBank className='h-4 w-4' />;
    case 'real-estate':
      return <Home className='h-4 w-4' />;
    case 'crypto':
      return <Coins className='h-4 w-4' />;
    case 'alternative':
      return <Building2 className='h-4 w-4' />;
    case 'liability':
      return <CreditCard className='h-4 w-4' />;
    default:
      return <Wallet className='h-4 w-4' />;
  }
};

export function AccountListItem({ account, onSelect, isSelected }: AccountListItemProps) {
  const defaultCurrency = useDefaultCurrency();

  return (
    <div
      className={`hover:bg-muted/40 group relative grid w-full min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_max-content] items-center gap-x-2 rounded-xl border p-3 transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-transparent'
      }`}
      onClick={() => onSelect(account)}
    >
      <div className='flex min-w-0 items-center gap-3'>
        <div className='shrink-0'>
          <div className='bg-fin-gray-800 text-fin-gray-400 rounded p-2'>
            {getAccountIcon(account.type)}
          </div>
        </div>
        <div className='min-w-0'>
          <div className='truncate font-medium' title={account.name}>
            {account.name}
          </div>
          <div className='text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs'>
            <span className='min-w-0 truncate' title={account.institution}>
              {account.institution}
            </span>
            <span className='flex shrink-0 items-center gap-1.5'>
              <AccountSourceBadge source={account.source} />
              {account.source === 'integration' && <SyncBadge status={account.syncStatus} />}
            </span>
          </div>
        </div>
      </div>

      <div className='text-right'>
        <div className='font-medium whitespace-nowrap tabular-nums'>
          {formatCurrency(account.balance, defaultCurrency)}
        </div>
        <div className='text-muted-foreground text-xs'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='cursor-help'>{getRelativeTime(account.lastUpdated || '')}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getFullDate(account.lastUpdated || '')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
